import { setProfile, track, trackOnExit } from 'lib/analyticsV2';
import { COUNTERS, EVENTS, FINAL_STATUS, SELFIE_OUTCOME } from './events';
import {
    endJourney,
    getCount,
    getJourney,
    getJourneyElapsedMs,
    isOnFlow,
    isOpen,
    recordIssue,
    recordStep,
    startJourney,
} from './journeyContext';

// Shared screens (errors, timeouts, attempts) also serve the GBG and KBA flows, so every call is a
// no-op unless the user reached them from a v2 screen.

// Returns whether the step was new, so a reload on the same screen repeats nothing tied to it.
export const trackStep = (step, { errorScreen } = {}) => {
    if (!isOnFlow()) return false;

    const previous = recordStep(step, errorScreen);

    if (!previous) return false;

    track(EVENTS.STEP_VIEWED, { step, ...previous, error_screen: errorScreen });
    return true;
};

export const trackFlowEvent = (event, properties) => {
    if (!isOnFlow()) return;

    track(event, properties);
};

export const trackAction = (action, properties) => {
    if (!isOnFlow()) return;

    track(EVENTS.ACTION_TAKEN, { action, step: getJourney().step, ...properties });
};

/**
 * `code` carries its source as a prefix (`ui.`, `regula.`, `aws.`, `backend.`, `http.`) so one
 * breakdown shows where a problem came from.
 */
export const trackIssue = ({ category, code, severity, ...properties }) => {
    if (!isOnFlow()) return;

    const { step, last_hint } = getJourney();
    const captureNumber = getCount(COUNTERS.CAPTURES);
    const sessionNumber = getCount(COUNTERS.SELFIE_SESSIONS);

    track(EVENTS.ISSUE_OCCURRED, {
        issue_category: category,
        issue_code: code,
        severity,
        step,
        journey_elapsed_ms: getJourneyElapsedMs(),
        last_hint,
        capture_number: captureNumber || undefined,
        session_number: sessionNumber || undefined,
        ...properties,
    });

    recordIssue({ code, severity });
};

export const trackJourneyStarted = (properties) => {
    startJourney();
    track(EVENTS.JOURNEY_STARTED, properties);
};

/**
 * `finalStatus` is left out on the result screen, which every outcome reaches: a selfie that failed
 * on its last attempt still carries on to the document result.
 */
export const trackJourneyEnded = ({ finalStatus, company, ...properties }) => {
    const summary = endJourney();

    if (!summary) return;

    const { last_issue_code, selfie_outcome, ...rest } = summary;
    const status =
        finalStatus ??
        (selfie_outcome === SELFIE_OUTCOME.FAILED
            ? FINAL_STATUS.SELFIE_FAILED
            : FINAL_STATUS.PASSED);

    track(EVENTS.JOURNEY_ENDED, {
        final_status: status,
        terminal_issue_code: status === FINAL_STATUS.PASSED ? undefined : last_issue_code,
        ...rest,
        ...properties,
    });

    setProfile({
        company_name: company?.name,
        company_uuid: company?.uuid,
    });
};

export const trackPageLeft = () => {
    if (!isOnFlow() || !isOpen()) return;

    const { step, last_hint, last_issue } = getJourney();

    trackOnExit(EVENTS.PAGE_LEFT, {
        step,
        journey_elapsed_ms: getJourneyElapsedMs(),
        last_hint,
        last_issue_code: last_issue?.code,
        last_issue_severity: last_issue?.severity,
    });
};
